import React, { useMemo } from "react";
import EventDetails from "./EventDetails";
import type { MatchTimelineEvent } from "./types";
import { Text } from "components/common/Text";
import { MINUTE_PILL_COLOR_CLASSES } from "./constants";

type MatchTimelineProps = {
  events: MatchTimelineEvent[];
  homeColor?: string;
  awayColor?: string;
  icons?: Record<string, React.ReactNode>;
  title?: string;
  header?: React.ReactNode;
  topMarker?: MatchTimelineEvent | null;
  bottomMarker?: MatchTimelineEvent | null;
};

const SPECIAL_TYPES = new Set([
  "full_time",
  "first_half_end",
  "extra_time",
  "whistle",
]);

const Divider = () => <div className="mx-[1vw] h-[1px] w-[20vw] bg-border" />;

const formatMinute = (event: MatchTimelineEvent): string => {
  if (event.minuteText) return event.minuteText;
  const minute = event.minute ?? 0;
  const extra = event.extraMinutes ?? 0;
  if (extra > 0) return `${minute}+${extra}'`;
  if (minute > 0) return `${minute}'`;
  return "";
};

const renderMinuteContent = (
  event: MatchTimelineEvent,
  icons?: Record<string, React.ReactNode>
) => {
  if (event.type === "full_time") {
    return (
      <>
        <Divider />
        FT
        <Divider />
      </>
    );
  }
  if (event.type === "first_half_end") {
    return (
      <>
        <Divider />
        FH
        <Divider />
      </>
    );
  }
  if (event.type === "whistle") {
    const icon = icons?.[event.iconKey ?? event.type] ?? null;
    return icon;
  }
  if (event.type === "extra_time" && event.extraMinutes) {
    return `+${event.extraMinutes}'`;
  }
  return event.minuteText ?? formatMinute(event);
};

const renderEventRow = (
  event: MatchTimelineEvent,
  index: number | string,
  colors: { home?: string; away?: string },
  icons?: Record<string, React.ReactNode>
) => {
  const minuteValue = Number(event.minute) || 0;
  const shouldCenter = minuteValue <= 0 && !SPECIAL_TYPES.has(event.type ?? "");

  const normalizedEvent = shouldCenter ? { ...event, team: undefined } : event;

  const centerText =
    event.minuteText ||
    event.title ||
    (event.subtitle && String(event.subtitle)) ||
    (event.raw != null ? String(event.raw) : "");

  const minuteContent = shouldCenter ? (
    <>
      <Divider />
      {centerText}
      <Divider />
    </>
  ) : (
    renderMinuteContent(normalizedEvent, icons)
  );

  const minuteType = (shouldCenter ? "center" : event.type) ?? "generic";
  const special = shouldCenter || SPECIAL_TYPES.has(event.type ?? "");

  return (
    <div
      key={index}
      className="relative flex h-[8vw] gap-[2vw] w-full items-center justify-between"
    >
      <EventDetails
        event={normalizedEvent}
        team="home"
        homeColor={colors.home}
        awayColor={colors.away}
        icons={icons}
      />

      <MinutePill special={special} type={minuteType}>
        {minuteContent}
      </MinutePill>

      <EventDetails
        event={normalizedEvent}
        team="away"
        homeColor={colors.home}
        awayColor={colors.away}
        icons={icons}
      />
    </div>
  );
};

const MatchTimeline: React.FC<MatchTimelineProps> = ({
  events,
  homeColor,
  awayColor,
  icons,
  title = "Match Timeline",
  header,
  topMarker,
  bottomMarker,
}) => {
  const colors = useMemo(
    () => ({ home: homeColor, away: awayColor }),
    [homeColor, awayColor]
  );

  return (
    <div className="flex w-full flex-col items-center text-white">
      {header}

      {title && (
        <div className="mb-[2vw] w-full border-b-[0.4vw] border-green pb-[0.5vw] text-center font-bold">
          <Text variant="timelineTitle">{title}</Text>
        </div>
      )}

      <div className="flex w-full flex-col gap-[1vw]">
        {topMarker &&
          renderEventRow(
            { ...topMarker, id: topMarker.id ?? "timeline-top-marker" },
            "timeline-top-marker",
            colors,
            icons
          )}

        {events.map((event, index) =>
          renderEventRow(event, event.id ?? index, colors, icons)
        )}

        {bottomMarker &&
          renderEventRow(
            {
              ...bottomMarker,
              id: bottomMarker.id ?? "timeline-bottom-marker",
            },
            "timeline-bottom-marker",
            colors,
            icons
          )}
      </div>
    </div>
  );
};

type MinutePillProps = {
  type: string;
  special: boolean;
  children: React.ReactNode;
};

function MinutePill({ type, special, children }: MinutePillProps) {
  const colorClass =
    type === "goal"
      ? MINUTE_PILL_COLOR_CLASSES.highlight
      : type === "whistle" || type === "full_time" || type === "first_half_end"
      ? MINUTE_PILL_COLOR_CLASSES.transparent
      : MINUTE_PILL_COLOR_CLASSES.neutral;

  return (
    <div
      className={`inline-flex w-[12vw] items-center justify-center rounded-full border-0 px-[1vw] py-[1vw] text-center font-bold leading-none ${colorClass} ${
        special ? "mt-[0.5vw]" : ""
      }`}
    >
      <Text variant="timelineMinutePill">{children}</Text>
    </div>
  );
}

export default MatchTimeline;
export type { MatchTimelineEvent };
